import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';

import { ReactComponent as Lock } from 'design_system/src/static/icons/lock-square.svg';
import { Button, Input, Typography } from 'design_system/src';

import { setPassword } from 'models/auth/controllers/auth.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';

import './auth.scss';

const validationSchema = yup.object({
	password: yup
		.string()
		.min(6, 'Contraseña debe tener al menos 6 caracteres')
		.required('Contraseña es requerida'),
	confirmPassword: yup
		.string()
		.oneOf([yup.ref('password')], 'Las contraseñas deben coincidir')
		.required('Por favor confirma tu contraseña'),
});

function ResetPassword() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [loading, setLoading] = useState<boolean>(false);
	const [token, setToken] = useState<string | null>(null);

	useEffect(() => {
		const tokenParam = searchParams.get('token');
		if (!tokenParam) {
			toasterNotification({
				msg: 'Enlace de restablecimiento de contraseña inválido. Por favor, solicita un nuevo restablecimiento de contraseña.',
				toasterType: toasterOptions.error,
			});
			navigate('/auth');
		} else {
			setToken(tokenParam);
		}
	}, [searchParams, navigate]);

	const formik = useFormik({
		initialValues: {
			password: '',
			confirmPassword: '',
		},
		validationSchema,
		onSubmit: async (values) => {
			if (!token) {
				return;
			}

			setLoading(true);
			try {
				await setPassword({
					token,
					password: values.password,
				});
				Swal.fire({
					title: '¡Éxito!',
					text: 'Tu contraseña ha sido restablecida correctamente. Por favor, inicia sesión con tu nueva contraseña.',
					icon: 'success',
					confirmButtonText: 'Ir a Iniciar sesión',
				}).then(() => {
					navigate('/auth');
				});
			} catch (error: any) {
				const errorMessage =
					extractErrorMessage(error) || 'Error resetting password';
				toasterNotification({
					msg: errorMessage,
					toasterType: toasterOptions.error,
				});
			} finally {
				setLoading(false);
			}
		},
	});

	if (!token) {
		return null;
	}

	return (
		<div className="root flex_center">
			<div
				className="
        dso_card 
        login_card 
        p_xxl 
        shadow_hard"
			>
				<img
					alt="Logo"
					src={`${process.env.PUBLIC_URL || ''}/sauron-logo.png`}
					className="logo center_x m_b_xxl"
				/>
				<form onSubmit={formik.handleSubmit}>
					<Typography
						scale="medium"
						weight="600"
						className=" m_b_md text_center"
						textColor="neutral_0"
					>
						Reset Password
					</Typography>

					<Input
						title="Nueva contraseña"
						type="password"
						name="password"
						placeholder="Ingrese su nueva contraseña"
						value={formik.values.password}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						icon={<Lock />}
						className="m_b_md dso_width_100_per"
					/>
					{formik.touched.password && formik.errors.password && (
						<Typography
							scale="small"
							weight="400"
							className="m_b_md text_center"
							textColor="primary_300"
						>
							{formik.errors.password}
						</Typography>
					)}

					<Input
						title="Confirmar contraseña"
						type="password"
						name="confirmPassword"
						placeholder="Confirme su nueva contraseña"
						value={formik.values.confirmPassword}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						icon={<Lock />}
						className="m_b_xxl dso_width_100_per"
					/>
					{formik.touched.confirmPassword && formik.errors.confirmPassword && (
						<Typography
							scale="small"
							weight="400"
							className="m_b_md text_center"
							textColor="primary_300"
						>
							{formik.errors.confirmPassword}
						</Typography>
					)}

					<Button
						type="submit"
						disabled={loading || !formik.isValid}
						scale="small"
						className="w_100_per m_b_md m_t_xxs"
						icon={<Lock />}
					>
						{loading ? (
							<CircularProgress className="loading" />
						) : (
							'Restablecer contraseña'
						)}
					</Button>
				</form>
			</div>
		</div>
	);
}

export default ResetPassword;
